import React, { useEffect, useState, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import apiClient from 'helpers/api-client';
import toast from 'helpers/toast';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
} from '@companycam/companycam-events';
import ExportModal from './modals/ExportModal';
import ErrorModal from './modals/ErrorModal';
import ExportCompleteModal from './modals/ExportCompleteModal';
import { exportStates } from 'components/projects/taskList/constants';

const PDF_UPLOAD_CANCELED = 'pdf-upload-canceled';

const initialState = {
  includePhotos: true,
  includePhotoDetails: true,
  photosPerRow: 3,
  saveToProjectDocuments: false,
  includeSkippedSections: true,
};

function toggleReducer(state, action) {
  switch (action.type) {
    case 'includePhotos':
      if (action.value === false) {
        return {
          ...state,
          includePhotos: false,
          includePhotoDetails: false,
        };
      }
      return { ...state, includePhotos: !state.includePhotos };
    case 'includePhotoDetails':
      return {
        ...state,
        includePhotoDetails: !state.includePhotoDetails,
      };
    case 'photosPerRow':
      return { ...state, photosPerRow: state.photosPerRow === 2 ? 3 : 2 };
    case 'saveToProjectDocuments':
      return {
        ...state,
        saveToProjectDocuments: !state.saveToProjectDocuments,
      };
    case 'includeSkippedSections':
      return {
        ...state,
        includeSkippedSections: !state.includeSkippedSections,
      };
    case 'reset':
      return initialState;
    default:
      throw new Error();
  }
}

const messages = defineMessages({
  exportPdfToastTitle: {
    id: 'ExportTaskList.exportPdfToastTitle',
    defaultMessage: `We'll notify you when your PDF is ready.`,
    description: 'Toast title when exporting PDF',
  },
  exportPdfToastMessage: {
    id: 'ExportTaskList.exportPdfToastMessage',
    defaultMessage: `We're building your PDF - when it's finished, we'll send you a notification and you can download it from there.`,
    description: 'Toast message when exporting PDF',
  },
});

const ExportTaskList = ({ taskList, exportState, setExportState }) => {
  const { formatMessage } = useIntl();
  const [toggleState, toggleDispatch] = useReducer(toggleReducer, initialState);
  const {
    includePhotos,
    includePhotoDetails,
    photosPerRow,
    saveToProjectDocuments,
    includeSkippedSections,
  } = toggleState;

  const [createPdfLoading, setCreatePdfLoading] = useState(false);

  useEffect(() => {
    if (!includePhotos) {
      toggleDispatch({ type: 'includePhotos', value: false });
    }
  }, [includePhotos]);

  useEffect(() => {
    if (exportState === exportStates.NONE) {
      toggleDispatch({ type: 'reset' });
    }
  }, [exportState]);

  const handleExportClick = () => {
    setCreatePdfLoading(true);
    apiClient
      .post(`task_lists/${taskList.uuid}/async_upload`, {
        include_photos: includePhotos,
        include_photo_capture_details: includePhotoDetails,
        photos_per_row: photosPerRow,
        save_to_documents: saveToProjectDocuments,
        include_skipped_sections: includeSkippedSections,
      })
      .then(() => {
        CCEvents.logEvent(EventNames.EXPORT_TASKLIST_PDF, {
          [EventPropertyKeys.PHOTOS_INCLUDED]: includePhotos,
          [EventPropertyKeys.PHOTO_DETAILS_INCLUDED]: includePhotoDetails,
          [EventPropertyKeys.PHOTOS_PER_ROW]: photosPerRow,
          [EventPropertyKeys.SAVE_TO_DOCS]: saveToProjectDocuments,
        });

        toast.show({
          title: formatMessage(messages.exportPdfToastTitle),
          message: formatMessage(messages.exportPdfToastMessage),
          duration: 15,
        });

        setExportState(exportStates.NONE);
      })
      .catch((e) => {
        if (e.messages !== PDF_UPLOAD_CANCELED) {
          setExportState(exportStates.ERROR);
        }
      })
      .finally(() => {
        setCreatePdfLoading(false);
      });
  };

  return (
    <>
      <ExportModal
        isOpen={exportState === exportStates.EXPORT}
        onRequestClose={() => {
          apiClient.handleCancelRequest(PDF_UPLOAD_CANCELED);
          setExportState(exportStates.NONE);
        }}
        onExportClick={handleExportClick}
        toggleState={toggleState}
        toggleDispatch={toggleDispatch}
        loading={createPdfLoading}
      />

      <ExportCompleteModal
        isOpen={exportState === exportStates.SUCCESS}
        onRequestClose={() => setExportState(exportStates.NONE)}
        savedToDocuments={saveToProjectDocuments}
      />

      <ErrorModal
        isOpen={exportState === exportStates.ERROR}
        onRequestClose={() => setExportState(exportStates.NONE)}
      />
    </>
  );
};

ExportTaskList.propTypes = {
  taskList: PropTypes.object,
  exportState: PropTypes.string,
  setExportState: PropTypes.func,
};

export default ExportTaskList;
